'use client';
import { sendGAEvent } from '@next/third-parties/google';
import { SITE } from '@/app/constants';
import type { OrderInfo } from '@/app/types';

const isBrowser = typeof window !== 'undefined';

interface GoogleEnhancedConversionUserData {
  email?: string;
  phoneNumber?: string;
}

// ── Bing ──────────────────────────────────────────────────────────────────────

export const setBingEnhancedConversion = (email: string, phone?: string) => {
  if (!isBrowser || !window.uetq) return;
  window.uetq.push('set', { pid: { em: email, ph: phone } });
};

export const trackBingSignUp = () => {
  if (!isBrowser || !window.uetq) return;
  window.uetq.push('event', 'signup', {});
};

export const trackBingPurchase = (value: number, productType?: OrderInfo['product_type']) => {
  if (!isBrowser || !window.uetq || productType !== 'subscription') return;
  const currency = SITE === 'china' ? 'CNY' : 'USD';
  window.uetq.push('event', 'purchase', { revenue_value: value / 100, currency });
};

// ── Meta ──────────────────────────────────────────────────────────────────────

export const getMetaPixelCookie = (cookieName: '_fbp' | '_fbc'): string => {
  if (!isBrowser) return '';
  const cookie = document.cookie.split('; ').find((row) => row.startsWith(`${cookieName}=`));
  return cookie ? cookie.split('=')[1] : '';
};

export const getMetaPixelCookies = (): { fbp: string; fbc: string } => ({
  fbp: getMetaPixelCookie('_fbp'),
  fbc: getMetaPixelCookie('_fbc'),
});

export const trackMetaCompleteRegistration = () => {
  if (!isBrowser || !window.fbq) return;
  window.fbq('track', 'CompleteRegistration');
};

export const trackMetaSubscribe = (value: number) => {
  if (!isBrowser || !window.fbq) return;
  window.fbq('track', 'Subscribe', { value: value.toFixed(2), currency: 'USD' });
};

// ── Yahoo ─────────────────────────────────────────────────────────────────────

const YAHOO_PIXEL_ID = '1001362811';
const YAHOO_PIXEL_TYPE = 'yss_conversion';
const YAHOO_PIXEL_PURCHASE_LABEL = 'r0g8CP6BwK0aENGIko4_';
const YAHOO_PIXEL_SIGNUP_LABEL = 'SZFGCJ2U0K0aENGIko4_';

export const trackYahooPurchase = (value: number) => {
  if (!isBrowser || !window.ytag) return;
  window.ytag({
    type: YAHOO_PIXEL_TYPE,
    config: {
      yahoo_conversion_id: YAHOO_PIXEL_ID,
      yahoo_conversion_label: YAHOO_PIXEL_PURCHASE_LABEL,
      yahoo_conversion_value: value,
    },
  });
};

export const trackYahooSignUp = () => {
  if (!isBrowser || !window.ytag) return;
  window.ytag({
    type: YAHOO_PIXEL_TYPE,
    config: {
      yahoo_conversion_id: YAHOO_PIXEL_ID,
      yahoo_conversion_label: YAHOO_PIXEL_SIGNUP_LABEL,
      yahoo_conversion_value: '0',
    },
  });
};

// ── Google Analytics 4 ────────────────────────────────────────────────────────

export const trackGASignUp = (userData?: GoogleEnhancedConversionUserData) => {
  if (!isBrowser) return;

  if (userData) {
    setGoogleEnhancedConversionUserData(userData);
  }

  sendGAEvent('event', 'sign_up', { method: 'email' });
};

export const setGoogleEnhancedConversionUserData = ({ email, phoneNumber }: GoogleEnhancedConversionUserData) => {
  if (!isBrowser || SITE !== 'global') return;

  const normalizedEmail = email?.trim().toLowerCase();
  if (!normalizedEmail) return;

  const normalizedPhoneNumber = phoneNumber?.trim();
  const isE164PhoneNumber = normalizedPhoneNumber && /^\+[1-9]\d{9,13}$/.test(normalizedPhoneNumber);

  // Google tag receives unhashed first-party data here and performs normalization and SHA-256 hashing before upload.
  // Consent enforcement belongs to the site's Google Consent Mode / GTM configuration.
  sendGAEvent('set', 'user_data', {
    email: normalizedEmail,
    ...(isE164PhoneNumber ? { phone_number: normalizedPhoneNumber } : {}),
  });
};

export const trackGAPurchase = (data: OrderInfo, userData?: GoogleEnhancedConversionUserData) => {
  if (!isBrowser || !data.id) return;
  const currency = SITE === 'china' ? 'CNY' : 'USD';
  const value = data.amount / 100;
  const itemId = data.plan_type || data.product_type;

  if (userData) {
    setGoogleEnhancedConversionUserData(userData);
  }

  sendGAEvent('event', 'purchase', {
    transaction_id: data.id,
    value,
    currency,
    items: [
      {
        item_id: itemId,
        item_name: itemId,
        item_category: data.product_type,
        price: value,
        quantity: 1,
      },
    ],
  });
};
